import axios from 'axios';
import { clearColumnCache } from '../../services/columnCache';
import dbQueries from '../../config/dbQueries'; // dbQueries 임포트
// import axiosInstance from '../../App'; // App.js에서 export 하지 않음. 아래 localAxiosInstance 사용.

export const SET_CONNECTIONS = 'SET_CONNECTIONS';
export const FETCH_SCHEMAS_START = 'FETCH_SCHEMAS_START';
export const FETCH_SCHEMAS_SUCCESS = 'FETCH_SCHEMAS_SUCCESS';
export const FETCH_SCHEMAS_FAILURE = 'FETCH_SCHEMAS_FAILURE';
export const INVALIDATE_SCHEMAS = 'INVALIDATE_SCHEMAS'; // 캐시 무효화 (수동 새로고침용)
export const FETCH_COMMENTS_START = 'FETCH_COMMENTS_START';
export const FETCH_COMMENTS_SUCCESS = 'FETCH_COMMENTS_SUCCESS';
export const FETCH_COMMENTS_FAILURE = 'FETCH_COMMENTS_FAILURE';

// 서버에서 connection 목록을 가져오는 액션 (현재는 사용하지 않으나 구조는 유지)
export const setConnections = (connections) => ({
    type: SET_CONNECTIONS,
    payload: connections,
});

export const fetchSchemasStart = (connectionId) => ({
    type: FETCH_SCHEMAS_START,
    payload: { connectionId },
});

export const fetchSchemasSuccess = (connectionId, schemas) => ({
    type: FETCH_SCHEMAS_SUCCESS,
    payload: { connectionId, schemas },
});

export const fetchSchemasFailure = (connectionId, error) => ({
    type: FETCH_SCHEMAS_FAILURE,
    payload: { connectionId, error },
});

// 활성 connectionId 관리
let _activeConnectionId = null;
export const setActiveConnectionIdForSchema = (id) => { _activeConnectionId = id; };
/**
 * 지금 헤더에서 고른 연결. 챌린지 채점도 이 연결에서 돈다(로컬 챌린지일 때) —
 * 내부망에는 백엔드 시스템 풀이 없어 다른 선택지가 없고, 편집기와 같은 연결이라 일관된다.
 */
export const getActiveConnectionId = () => _activeConnectionId;

// 스키마 조회용 axios 인스턴스 (인증 없음)
const localAxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/sql-developer',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    },
    timeout: 15000,
    validateStatus: function (status) {
        return status < 500;
    }
});

// 요청 시 connectionId 자동 주입 — 부르는 쪽이 연결을 정했으면 덮지 않는다(App 의 axiosInstance 와 같은 규칙).
// 덮어쓰면 「연결 X 의 스키마를 읽어라」 가 탐색기 연결로 나간다.
localAxiosInstance.interceptors.request.use((config) => {
    if (_activeConnectionId && config.data && typeof config.data === 'object' && config.data.connectionId == null) {
        config.data.connectionId = _activeConnectionId;
    }
    return config;
});

// 캐시된 스키마 트리(children) 를 비워 재조회를 강제. 함께 1초 throttle 키도 리셋.
export const invalidateSchemas = (connectionId) => ({
    type: INVALIDATE_SCHEMAS,
    payload: { connectionId },
});

// 사용자 명시 새로고침 — 캐시 무효화 후 fetch 재실행.
export const refreshSchemasForConnection = (connectionId, dbType) => async (dispatch) => {
    if (!connectionId) return;
    clearColumnCache(connectionId);   // 자동완성의 컬럼 캐시도 같이(테이블이 바뀌었을 수 있다)
    // 1초 throttle key 리셋
    try { delete window[`lastRequest_${connectionId}`]; } catch (_) {}
    dispatch(invalidateSchemas(connectionId));
    return dispatch(fetchSchemasForConnection(connectionId, dbType));
};

export const fetchSchemasForConnection = (connectionId, dbType) => async (dispatch, getState) => {
    console.log(`[Action] fetchSchemasForConnection: Called for connectionId=${connectionId}, dbType=${dbType}`);
    
    // 중복 요청 방지: 이미 로딩 중이거나 스키마가 이미 로드된 경우 중단
    const { schema } = getState();
    const connectionDetails = schema.connections.find(c => c.id === connectionId);
    
    if (!connectionDetails) {
        console.error(`[Action] fetchSchemasForConnection: Connection details not found for ID: ${connectionId}`);
        return;
    }
    
    // 이미 로딩 중이거나 스키마가 이미 로드된 경우 중단
    if (connectionDetails.isLoadingSchemas) {
        console.log(`[Action] fetchSchemasForConnection: Already loading schemas for ${connectionId}, skipping`);
        return;
    }
    
    if (connectionDetails.children && connectionDetails.children.length > 0) {
        console.log(`[Action] fetchSchemasForConnection: Schemas already loaded for ${connectionId}, skipping`);
        return;
    }
    
    // 추가 안전장치: 같은 연결에 대해 1초 이내 중복 요청 방지
    const now = Date.now();
    const lastRequestKey = `lastRequest_${connectionId}`;
    const lastRequestTime = window[lastRequestKey] || 0;
    if (now - lastRequestTime < 1000) {
        console.log(`[Action] fetchSchemasForConnection: Request too soon for ${connectionId}, skipping`);
        return;
    }
    window[lastRequestKey] = now;

    dispatch(fetchSchemasStart(connectionId));
    console.log(`[Action] fetchSchemasForConnection: Dispatched FETCH_SCHEMAS_START for ${connectionId}`);

    try {
        console.log(`[Action] fetchSchemasForConnection: Found connectionDetails for ${connectionId}`, connectionDetails);

        let schemasWithTables = [];

        const queriesForDb = dbQueries[dbType];
        if (!queriesForDb) {
            console.error(`[Action] fetchSchemasForConnection: No queries defined for dbType: ${dbType}`);
            const msg = `Unsupported DB type: ${dbType}`;
            dispatch(fetchSchemasFailure(connectionId, msg));
            return { success: false, error: msg };
        }

        // 1. 스키마 목록 가져오기
        console.log(`[Action] fetchSchemasForConnection: Attempting to fetch schemas for ${dbType} - ${connectionId}`);
        const schemaQuery = queriesForDb.fetchSchemas;
        if (!schemaQuery) {
            console.error(`[Action] fetchSchemasForConnection: Schema query not defined for dbType: ${dbType}`);
            const msg = `Schema query not defined for: ${dbType}`;
            dispatch(fetchSchemasFailure(connectionId, msg));
            return { success: false, error: msg };
        }
        console.log(`[Action] fetchSchemasForConnection: Schema query: ${schemaQuery}`);
            
        // 사용자 인증 정보 가져오기
        const { auth } = getState();
        const user = auth.user;

        const schemaResponse = await localAxiosInstance.post('/execute', {
            query: schemaQuery,
            usrid: user?.email || user?.id || 'standalone',
            connectionId // 명시적 주입 (interceptor 의존 X)
        });
        console.log(`[Action] fetchSchemasForConnection: Schemas response raw:`, schemaResponse);

        let rawSchemas = [];
        if (schemaResponse.data && Array.isArray(schemaResponse.data.rows)) { 
            rawSchemas = schemaResponse.data.rows;
        } else if (Array.isArray(schemaResponse.data)) { 
            rawSchemas = schemaResponse.data;
        } else {
            console.error("[Action] fetchSchemasForConnection: Unexpected schema response structure:", schemaResponse.data);
            throw new Error('Failed to parse schema data.');
        }

        if (rawSchemas.length === 0) {
            console.log(`[Action] fetchSchemasForConnection: No schemas found for ${dbType} - ${connectionId}`);
            dispatch(fetchSchemasSuccess(connectionId, [])); // 빈 배열로 성공 처리
            return;
        }

        const schemaNames = rawSchemas.map(s => s.SCHEMA_NAME || s.schema_name || s.name).filter(Boolean);
        if (schemaNames.length === 0) {
            console.log(`[Action] fetchSchemasForConnection: All schema names are empty for ${dbType} - ${connectionId}`);
            dispatch(fetchSchemasSuccess(connectionId, rawSchemas.map(s => ({ name: s.SCHEMA_NAME || s.schema_name || s.name || 'UnknownSchema', tables: [] }))));
            return;
        }

        // 2. 모든 스키마의 테이블 정보 한 번에 가져오기
        const tablesQueryFunction = queriesForDb.fetchAllTablesWithOwners;
        if (!tablesQueryFunction) {
            console.warn(`[Action] fetchSchemasForConnection: fetchAllTablesWithOwners query function not defined for dbType: ${dbType}. Schemas will have no tables.`);
            // 스키마 정보만이라도 넘겨주기
            schemasWithTables = rawSchemas.map(s => ({ name: s.SCHEMA_NAME || s.schema_name || s.name, tables: [] }));
        } else {
            const allTablesQuery = tablesQueryFunction(schemaNames);
            console.log(`[Action] fetchSchemasForConnection: Fetching all tables for schemas (${schemaNames.join(',')}) with query: ${allTablesQuery}`);
            
            const allTablesResponse = await localAxiosInstance.post('/execute', {
                query: allTablesQuery,
                usrid: user?.email || user?.id || 'standalone',
                connectionId
            });
            let allTables = [];
            if (allTablesResponse.data && Array.isArray(allTablesResponse.data.rows)) {
                allTables = allTablesResponse.data.rows;
            } else if (Array.isArray(allTablesResponse.data)) {
                allTables = allTablesResponse.data;
            } else {
                console.warn(`[Action] fetchSchemasForConnection: Unexpected allTables response for schemas:`, allTablesResponse.data);
            }

            // 테이블들을 스키마별로 그룹화
            const tablesBySchema = allTables.reduce((acc, table) => {
                const schemaName = table.SCHEMA_NAME || table.schema_name; // 백엔드에서 반환된 스키마 이름 컬럼
                const tableName = table.TABLE_NAME || table.table_name;
                if (schemaName && tableName) {
                    if (!acc[schemaName]) {
                        acc[schemaName] = [];
                    }
                    acc[schemaName].push({ name: tableName });
                }
                return acc;
            }, {});

            // 3. 뷰 목록 가져오기
            let viewsBySchema = {};
            const viewsQueryFn = queriesForDb.fetchAllViewsWithOwners;
            if (viewsQueryFn) {
                try {
                    const viewsResp = await localAxiosInstance.post('/execute', {
                        query: viewsQueryFn(schemaNames),
                        usrid: user?.email || user?.id || 'standalone',
                        connectionId
                    });
                    const allViews = viewsResp.data?.rows || (Array.isArray(viewsResp.data) ? viewsResp.data : []);
                    viewsBySchema = allViews.reduce((acc, v) => {
                        const s = v.SCHEMA_NAME || v.schema_name;
                        const n = v.VIEW_NAME || v.view_name;
                        if (s && n) { if (!acc[s]) acc[s] = []; acc[s].push({ name: n }); }
                        return acc;
                    }, {});
                } catch (e) { console.warn('[Action] Views fetch failed:', e.message); }
            }

            // 4. 프로시저/함수 목록 가져오기
            let procsBySchema = {};
            const procsQueryFn = queriesForDb.fetchAllProceduresWithOwners;
            if (procsQueryFn) {
                try {
                    const procsResp = await localAxiosInstance.post('/execute', {
                        query: procsQueryFn(schemaNames),
                        usrid: user?.email || user?.id || 'standalone',
                        connectionId
                    });
                    const allProcs = procsResp.data?.rows || (Array.isArray(procsResp.data) ? procsResp.data : []);
                    procsBySchema = allProcs.reduce((acc, p) => {
                        const s = p.SCHEMA_NAME || p.schema_name;
                        const n = p.OBJECT_NAME || p.object_name;
                        const t = p.OBJECT_TYPE || p.object_type;
                        // PG overloading 대응: OID/args 가 있으면 unique 식별자로 동봉.
                        const oid = p.OBJECT_OID || p.object_oid;
                        const args = p.OBJECT_ARGS || p.object_args;
                        if (s && n) { if (!acc[s]) acc[s] = []; acc[s].push({ name: n, objectType: t, oid, args }); }
                        return acc;
                    }, {});
                } catch (e) { console.warn('[Action] Procedures fetch failed:', e.message); }
            }

            // 최종 schemasData 구성 (테이블 + 뷰 + 프로시저/함수)
            schemasWithTables = rawSchemas.map(schemaRow => {
                const schemaName = schemaRow.SCHEMA_NAME || schemaRow.schema_name || schemaRow.name;
                return {
                    name: schemaName,
                    tables: tablesBySchema[schemaName] || [],
                    views: viewsBySchema[schemaName] || [],
                    procedures: procsBySchema[schemaName] || [],
                };
            });
        }
                
        console.log(`[Action] fetchSchemasForConnection: Parsed schemasWithTables for ${dbType}:`, schemasWithTables);
        dispatch(fetchSchemasSuccess(connectionId, schemasWithTables));

        // 스키마 로드 성공 후 주석 데이터도 자동 로드
        if (schemasWithTables.length > 0) {
            const schemaNames = schemasWithTables.map(s => s.name);
            dispatch(fetchCommentsForSchemas(schemaNames, connectionId));
        }
        return { success: true, schemaCount: schemasWithTables.length };
    } catch (error) {
        console.error(`[Action] fetchSchemasForConnection: General error for ${connectionId}:`, error.response || error.message || error);
        const msg = error.message || `Failed to fetch schemas/tables for ${dbType} DB`;
        dispatch(fetchSchemasFailure(connectionId, msg));
        return { success: false, error: msg };
    }
};

// 테이블/컬럼 주석 조회 액션
// 연결마다 하나씩만 — 예전에는 전역 「불러오는 중」 하나로 막아, 연결 둘이 함께 열리면 뒤의 연결은 주석을 영영 받지 못했다(2026-10-29)
const commentsInFlight = new Set();
export const fetchCommentsForSchemas = (schemaNames, connectionId) => async (dispatch) => {
    const flightKey = String(connectionId || '');
    if (commentsInFlight.has(flightKey)) return;
    commentsInFlight.add(flightKey);

    console.log(`[Action] fetchCommentsForSchemas: Loading comments for schemas:`, schemaNames);
    dispatch({ type: FETCH_COMMENTS_START });

    try {
        const response = await localAxiosInstance.post('/get-comments', {
            schemas: schemaNames,
            connectionId
        });

        if (response.data && response.data.tableComments) {
            dispatch({
                type: FETCH_COMMENTS_SUCCESS,
                payload: {
                    tableComments: response.data.tableComments,
                    columnComments: response.data.columnComments,
                },
            });
            const tableCount = Object.keys(response.data.tableComments).length;
            console.log(`[Action] fetchCommentsForSchemas: Loaded ${tableCount} table comments`);
        } else {
            dispatch({ type: FETCH_COMMENTS_FAILURE });
        }
    } catch (error) {
        console.error(`[Action] fetchCommentsForSchemas: Error:`, error.message);
        dispatch({ type: FETCH_COMMENTS_FAILURE });
    } finally {
        commentsInFlight.delete(flightKey);
    }
};